import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, 
  School, 
  Users, 
  Calendar, 
  FileDown,
  Clock,
  GraduationCap,
  Shield,
  AlertTriangle,
  TrendingUp,
  RefreshCw,
  Bell,
  CheckCircle,
  XCircle,
  BarChart3
} from 'lucide-react';

const AnalyticsDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [schoolDetails, setSchoolDetails] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    try {
      const [overviewRes, schoolsRes] = await Promise.all([
        api.get('/admin/analytics/overview'),
        api.get('/admin/analytics/schools?limit=10')
      ]);
      setAnalytics(overviewRes.data);
      setSchoolDetails(schoolsRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const triggerNotificationCheck = async () => {
    setSendingNotifications(true);
    try {
      const response = await api.post('/admin/notifications/check-expiry');
      if (response.data.notifications_sent > 0) {
        toast.success(`Sent ${response.data.notifications_sent} notification(s)`);
      } else {
        toast.info('No expiry notifications needed at this time');
      }
    } catch (error) {
      toast.error('Failed to send notifications');
    } finally {
      setSendingNotifications(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  const { schools, users, usage } = analytics || {};

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="analytics-dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
            <p className="text-slate-500">
              System-wide metrics and reporting
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => fetchAnalytics(true)} 
              disabled={refreshing}
              data-testid="refresh-analytics-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={triggerNotificationCheck}
              disabled={sendingNotifications}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="send-notifications-btn"
            >
              {sendingNotifications ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Bell className="h-4 w-4 mr-2" />
              )}
              Check Expiry Notifications
            </Button>
          </div>
        </div>

        {/* School Metrics */}
        <div>
          <h2 className="font-outfit text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <School className="h-5 w-5 text-indigo-600" />
            School Metrics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <MetricCard 
              label="Total Schools" 
              value={schools?.total_schools || 0} 
              icon={School}
              color="indigo"
            />
            <MetricCard 
              label="Active" 
              value={schools?.active_schools || 0} 
              icon={CheckCircle}
              color="emerald"
            />
            <MetricCard 
              label="Suspended" 
              value={schools?.suspended_schools || 0} 
              icon={XCircle}
              color="red"
            />
            <MetricCard 
              label="Trial" 
              value={schools?.trial_schools || 0} 
              icon={Clock}
              color="amber"
            />
            <MetricCard 
              label="Paid" 
              value={schools?.paid_schools || 0} 
              icon={TrendingUp}
              color="emerald"
            />
            <MetricCard 
              label="Expiring Soon" 
              value={schools?.expiring_soon || 0} 
              icon={AlertTriangle}
              color="orange"
              highlight={schools?.expiring_soon > 0}
            />
            <MetricCard 
              label="Expired" 
              value={schools?.expired || 0} 
              icon={XCircle}
              color="red"
              highlight={schools?.expired > 0}
            />
          </div>
        </div>

        {/* User Metrics */}
        <div>
          <h2 className="font-outfit text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            User Metrics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <MetricCard 
              label="Total Users" 
              value={users?.total_users || 0} 
              icon={Users}
              color="indigo"
            />
            <MetricCard 
              label="System Admins" 
              value={users?.system_admins || 0} 
              icon={Shield}
              color="purple"
            />
            <MetricCard 
              label="School Admins" 
              value={users?.school_admins || 0} 
              icon={Users}
              color="blue"
            />
            <MetricCard 
              label="School Users" 
              value={users?.school_users || 0} 
              icon={Users}
              color="slate"
            />
            <MetricCard 
              label="MFA Enabled" 
              value={users?.mfa_enabled_count || 0} 
              icon={Shield}
              color="emerald"
              subtitle={`${users?.mfa_adoption_rate || 0}% adoption`}
            />
            <MetricCard 
              label="Logins (7d)" 
              value={users?.recent_logins_7d || 0} 
              icon={TrendingUp}
              color="cyan"
            />
          </div>
        </div>

        {/* Usage Metrics */}
        <div>
          <h2 className="font-outfit text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-600" />
            Usage Metrics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <MetricCard 
              label="Conference Sets" 
              value={usage?.total_conference_sets || 0} 
              icon={Calendar}
              color="indigo"
            />
            <MetricCard 
              label="Schedule Sets" 
              value={usage?.total_schedule_sets || 0} 
              icon={Clock}
              color="purple"
            />
            <MetricCard 
              label="Export Sets" 
              value={usage?.total_export_sets || 0} 
              icon={FileDown}
              color="blue"
            />
            <MetricCard 
              label="Total Teachers" 
              value={usage?.total_teachers || 0} 
              icon={GraduationCap}
              color="emerald"
            />
            <MetricCard 
              label="Syncs (30d)" 
              value={usage?.veracross_syncs_30d || 0} 
              icon={RefreshCw}
              color="cyan"
            />
            <MetricCard 
              label="Exports (30d)" 
              value={usage?.exports_generated_30d || 0} 
              icon={FileDown}
              color="amber"
            />
          </div>
        </div>

        {/* Top Schools Table */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <School className="h-5 w-5 text-indigo-600" />
              Top Schools by Usage
            </CardTitle>
            <CardDescription>
              Schools ranked by number of users
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium text-slate-600">School</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Status</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">License</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Users</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Teachers</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Conferences</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Schedules</th>
                    <th className="text-center py-3 px-2 font-medium text-slate-600">Exports</th>
                  </tr>
                </thead>
                <tbody>
                  {schoolDetails.map((school, index) => (
                    <tr key={school.school_id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-2">
                        <span className="font-medium text-slate-900">{school.school_name}</span>
                      </td>
                      <td className="text-center py-3 px-2">
                        <Badge variant={school.status === 'active' ? 'default' : 'destructive'}>
                          {school.status}
                        </Badge>
                      </td>
                      <td className="text-center py-3 px-2">
                        <Badge variant={
                          school.license_status === 'active' ? 'default' : 
                          school.license_status === 'trial' ? 'secondary' : 'destructive'
                        }>
                          {school.license_status}
                        </Badge>
                      </td>
                      <td className="text-center py-3 px-2 font-medium">{school.user_count}</td>
                      <td className="text-center py-3 px-2">{school.teachers}</td>
                      <td className="text-center py-3 px-2">{school.conference_sets}</td>
                      <td className="text-center py-3 px-2">{school.schedule_sets}</td>
                      <td className="text-center py-3 px-2">{school.export_sets}</td>
                    </tr>
                  ))}
                  {schoolDetails.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-slate-500">
                        No schools found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Generated timestamp */}
        {analytics?.generated_at && (
          <p className="text-xs text-slate-400 text-right">
            Data generated: {new Date(analytics.generated_at).toLocaleString()}
          </p>
        )}
      </div>
    </DashboardLayout>
  );
};

// Metric Card Component
const MetricCard = ({ label, value, icon: Icon, color, subtitle, highlight }) => {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
    blue: 'bg-blue-50 text-blue-600',
    cyan: 'bg-cyan-50 text-cyan-600',
    slate: 'bg-slate-50 text-slate-600',
  };

  return (
    <Card className={highlight ? 'ring-2 ring-orange-400' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
        <div className="text-2xl font-bold text-slate-900">{value.toLocaleString()}</div>
        <div className="text-xs text-slate-500">{label}</div>
        {subtitle && (
          <div className="text-xs text-slate-400 mt-1">{subtitle}</div>
        )}
      </CardContent>
    </Card>
  );
};

export default AnalyticsDashboardPage;
